import React, { useState } from 'react';
import {
  Box, Paper, Typography, Grid, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, Divider, Alert, Button, Stack, TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useUpdateForm16PartB } from '../../../hooks/useForm16';

const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Leaf fields the admin may edit; everything else (totals, tax block, salary
// components) is computed and stays read-only. Must mirror PART_B_OVERRIDE_FIELDS
// on the backend.
const EDITABLE = new Set([
  'hra_exempt', 'lta_exempt', 'conveyance_exempt', 'other_sec10_exempt',
  'standard_deduction', 'entertainment_allowance', 'professional_tax',
  'house_property_loss',
  'sec_80c_total', 'sec_80ccc_total', 'sec_80ccd_1_total', 'sec_80ccd_1b_total',
  'sec_80ccd_2_total', 'sec_80d_total', 'sec_80e_total', 'sec_80g_total',
  'sec_80tta_total', 'other_chvia_total',
]);

const Row = ({ label, value, bold }) => (
  <TableRow>
    <TableCell sx={{ fontWeight: bold ? 700 : 400 }}>{label}</TableCell>
    <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>{fmt(value)}</TableCell>
  </TableRow>
);

export default function Form16PartB({ form16, editable = false, onSaved }) {
  const pb = form16.part_b;
  const update = useUpdateForm16PartB();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});

  if (!pb) {
    return <Alert severity="info">Part B snapshot is not generated yet. Click "Refresh from payroll" above.</Alert>;
  }

  const startEdit = () => {
    const d = {};
    EDITABLE.forEach((f) => { d[f] = pb[f] ?? 0; });
    setDraft(d);
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setDraft({}); };
  const setField = (f) => (e) => setDraft((prev) => ({ ...prev, [f]: e.target.value }));
  const save = () => {
    update.mutate({ id: form16.id, payload: draft }, {
      onSuccess: () => { setEditing(false); setDraft({}); onSaved?.(); },
    });
  };

  // Live preview of the summed totals while editing (pure arithmetic, mirrors the
  // backend recompute_part_b_totals). The tax block itself is recomputed on the
  // server at Save, so it keeps showing the last saved values until then.
  const D = (v) => Number(v || 0);
  const sec10Live = D(draft.hra_exempt) + D(draft.lta_exempt)
    + D(draft.conveyance_exempt) + D(draft.other_sec10_exempt);
  const sec16Live = D(draft.standard_deduction) + D(draft.entertainment_allowance)
    + D(draft.professional_tax);
  const incomeChargeableLive = D(pb.gross_salary) - sec10Live - sec16Live;
  const houseLossLive = Math.min(D(draft.house_property_loss), 200000);
  const gtiLive = incomeChargeableLive - houseLossLive;
  const combined80cLive = Math.min(
    D(draft.sec_80c_total) + D(draft.sec_80ccc_total) + D(draft.sec_80ccd_1_total), 150000,
  );
  const aggregateLive = combined80cLive + D(draft.sec_80ccd_1b_total) + D(draft.sec_80ccd_2_total)
    + D(draft.sec_80d_total) + D(draft.sec_80e_total) + D(draft.sec_80g_total)
    + D(draft.sec_80tta_total) + D(draft.other_chvia_total);
  const totalIncomeLive = Math.floor(Math.max(gtiLive - aggregateLive, 0) / 10) * 10;

  // Render a row that becomes an input while editing (only for editable leaf
  // fields). IMPORTANT: this is a plain function called as {editableRow(...)},
  // NOT a nested <Component/>. A nested component is a fresh type on every render,
  // so React would remount the <TextField> each keystroke and it would lose focus.
  const editableRow = (label, field) => {
    if (editing && EDITABLE.has(field)) {
      return (
        <TableRow key={field}>
          <TableCell>{label}</TableCell>
          <TableCell align="right">
            <TextField
              size="small" type="number" value={draft[field] ?? ''}
              onChange={setField(field)} disabled={update.isPending}
              inputProps={{ style: { textAlign: 'right', padding: '4px 8px' } }}
              sx={{ width: 150 }}
            />
          </TableCell>
        </TableRow>
      );
    }
    return <Row key={field} label={label} value={pb[field]} />;
  };

  return (
    <Box>
      {editable && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 1 }}>
          {!editing ? (
            <Button size="small" startIcon={<EditIcon />} onClick={startEdit}>
              Edit exemptions & deductions
            </Button>
          ) : (
            <>
              <Button size="small" startIcon={<CloseIcon />} onClick={cancel} disabled={update.isPending}>
                Cancel
              </Button>
              <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </Stack>
      )}
      {editing && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Editing the leaf values below. Totals and the tax block recompute automatically on Save,
          and your edits stick through future Refresh / Publish. Leave a field blank to revert it to the computed value.
        </Alert>
      )}
      {update.isError && <Alert severity="error" sx={{ mb: 1.5 }}>Failed to save edits.</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Salary Components (Section 17(1))</Typography>
            <Table size="small">
              <TableBody>
                <Row label="Basic" value={pb.basic_salary} />
                <Row label="DA" value={pb.da_salary} />
                <Row label="HRA" value={pb.hra_salary} />
                <Row label="Conveyance" value={pb.conveyance_salary} />
                <Row label="Medical" value={pb.medical_salary} />
                <Row label="Special Allowance" value={pb.special_allowance_salary} />
                <Row label="Bonus" value={pb.bonus_salary} />
                <Row label="Other Allowances" value={pb.other_salary} />
                <Row label="Gross Salary (a)" value={pb.gross_salary} bold />
                <Row label="Perquisites u/s 17(2) (b)" value={pb.perquisites_17_2} />
                <Row label="Profits u/s 17(3) (c)" value={pb.profits_17_3} />
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Section 10 Exemptions / Section 16 Deductions</Typography>
            <Table size="small">
              <TableBody>
                {editableRow('HRA Exempt', 'hra_exempt')}
                {editableRow('LTA Exempt', 'lta_exempt')}
                {editableRow('Conveyance Exempt', 'conveyance_exempt')}
                {editableRow('Other Sec 10 Exempt', 'other_sec10_exempt')}
                <Row label="Total Sec 10" value={editing ? sec10Live : pb.total_sec10_exempt} bold />
                <TableRow><TableCell colSpan={2}><Divider /></TableCell></TableRow>
                {editableRow('Standard Deduction (Sec 16(ia))', 'standard_deduction')}
                {editableRow('Entertainment Allowance (Sec 16(ii))', 'entertainment_allowance')}
                {editableRow('Professional Tax (Sec 16(iii))', 'professional_tax')}
                <TableRow><TableCell colSpan={2}><Divider /></TableCell></TableRow>
                <Row label="Income Chargeable under Salaries" value={editing ? incomeChargeableLive : pb.income_chargeable_salaries} bold />
                {editableRow('Loss from House Property (Sec 24)', 'house_property_loss')}
                <Row label="Gross Total Income" value={editing ? gtiLive : pb.gross_total_income} bold />
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Chapter VI-A Deductions</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>Section</TableCell>
                    <TableCell align="right">Deductible Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editableRow('80C (LIC, PPF, ELSS, NSC, Tuition, Home Loan Principal, etc.) — capped 1.5L', 'sec_80c_total')}
                  {editableRow('80CCC — Pension fund contribution', 'sec_80ccc_total')}
                  {editableRow('80CCD(1) — Self contribution to NPS', 'sec_80ccd_1_total')}
                  {editableRow('80CCD(1B) — Additional NPS (cap 50k)', 'sec_80ccd_1b_total')}
                  {editableRow('80CCD(2) — Employer NPS contribution', 'sec_80ccd_2_total')}
                  {editableRow('80D — Health insurance', 'sec_80d_total')}
                  {editableRow('80E — Education loan interest', 'sec_80e_total')}
                  {editableRow('80G — Donations', 'sec_80g_total')}
                  {editableRow('80TTA — Savings interest (cap 10k)', 'sec_80tta_total')}
                  {editableRow('Other Chapter VI-A', 'other_chvia_total')}
                  <Row label="Aggregate of Chapter VI-A" value={editing ? aggregateLive : pb.aggregate_chvia} bold />
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Tax Computation{editing && <Typography component="span" variant="caption" color="text.secondary"> — tax recomputes on Save</Typography>}
            </Typography>
            <Table size="small">
              <TableBody>
                <Row label="Total Income (Gross Total − Chapter VI-A)" value={editing ? totalIncomeLive : pb.total_income} bold />
                <Row label="Tax on Total Income" value={pb.tax_on_total} />
                <Row label="Surcharge" value={pb.surcharge} />
                <Row label="Health & Education Cess @ 4%" value={pb.education_cess} />
                <Row label="Tax Payable" value={pb.tax_payable} bold />
                <Row label="Less: Relief u/s 89" value={pb.relief_89} />
                <Row label="Net Tax Payable" value={pb.net_tax_payable} bold />
                <Row label="TDS already deducted" value={pb.tds_deducted} />
                <Row label="Balance Tax Payable / (Refundable)" value={pb.tax_balance} bold />
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {pb.monthly_breakup?.rows?.length > 0 && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Month-wise Breakup ({pb.monthly_breakup.regime} regime)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>Month</TableCell>
                      <TableCell>Year</TableCell>
                      <TableCell align="right">Earnings</TableCell>
                      <TableCell align="right">Deductions</TableCell>
                      <TableCell align="right">Net Pay</TableCell>
                      <TableCell align="right">TDS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pb.monthly_breakup.rows.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.month}</TableCell>
                        <TableCell>{r.year}</TableCell>
                        <TableCell align="right">{fmt(r.earnings)}</TableCell>
                        <TableCell align="right">{fmt(r.deductions)}</TableCell>
                        <TableCell align="right">{fmt(r.net_pay)}</TableCell>
                        <TableCell align="right">{fmt(r.tds)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
